import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

// Async thunks
export const fetchInvoices = createAsyncThunk(
  'invoices/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiService.invoices.getAll(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchInvoiceById = createAsyncThunk(
  'invoices/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiService.invoices.getById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createInvoice = createAsyncThunk(
  'invoices/create',
  async (invoiceData, { rejectWithValue }) => {
    try {
      const response = await apiService.invoices.create(invoiceData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateInvoice = createAsyncThunk(
  'invoices/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiService.invoices.update(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteInvoice = createAsyncThunk(
  'invoices/delete',
  async (id, { rejectWithValue }) => {
    try {
      await apiService.invoices.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const addPayment = createAsyncThunk(
  'invoices/addPayment',
  async ({ invoiceId, paymentData }, { rejectWithValue }) => {
    try {
      const response = await apiService.invoices.addPayment(invoiceId, paymentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const sendInvoice = createAsyncThunk(
  'invoices/send',
  async ({ invoiceId, phoneNumberId, recipientPhone }, { rejectWithValue }) => {
    try {
      const response = await apiService.invoices.send(invoiceId, { phoneNumberId, recipientPhone });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const initialState = {
  invoices: [],
  currentInvoice: null,
  loading: false,
  sendLoading: false,
  error: null,
  total: 0,
  pagination: {
    page: 1,
    limit: 20,
  },
  filters: {
    search: '',
    status: 'all',
  },
};

const invoicesSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentInvoice: (state, action) => {
      state.currentInvoice = action.payload;
    },
    clearCurrentInvoice: (state) => {
      state.currentInvoice = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    // Fetch all invoices
    builder
      .addCase(fetchInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoices.fulfilled, (state, action) => {
        state.invoices = action.payload.data || [];
        state.loading = false;
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
          state.total = action.payload.pagination.total;
        }
      })
      .addCase(fetchInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch invoice by ID
    builder
      .addCase(fetchInvoiceById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceById.fulfilled, (state, action) => {
        state.currentInvoice = action.payload.data || action.payload;
        state.loading = false;
      })
      .addCase(fetchInvoiceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create invoice
    builder
      .addCase(createInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        const invoice = action.payload.data || action.payload;
        state.invoices.unshift(invoice);
        state.total += 1;
        state.loading = false;
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Update invoice
    builder
      .addCase(updateInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        const invoice = action.payload.data || action.payload;
        const index = state.invoices.findIndex(inv => inv._id === invoice._id);
        if (index !== -1) {
          state.invoices[index] = invoice;
        }
        if (state.currentInvoice?._id === invoice._id) {
          state.currentInvoice = invoice;
        }
        state.loading = false;
      })
      .addCase(updateInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Delete invoice
    builder
      .addCase(deleteInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.invoices = state.invoices.filter(inv => inv._id !== action.payload);
        if (state.currentInvoice?._id === action.payload) {
          state.currentInvoice = null;
        }
        state.total -= 1;
        state.loading = false;
      })
      .addCase(deleteInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Add payment
    builder
      .addCase(addPayment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addPayment.fulfilled, (state, action) => {
        const invoice = action.payload.data || action.payload;
        const index = state.invoices.findIndex(inv => inv._id === invoice._id);
        if (index !== -1) {
          state.invoices[index] = invoice;
        }
        if (state.currentInvoice?._id === invoice._id) {
          state.currentInvoice = invoice;
        }
        state.loading = false;
      })
      .addCase(addPayment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Send invoice via WhatsApp
    builder
      .addCase(sendInvoice.pending, (state) => {
        state.sendLoading = true;
        state.error = null;
      })
      .addCase(sendInvoice.fulfilled, (state, action) => {
        state.sendLoading = false;
        // Update the invoice status to 'sent' in the list
        const invoiceId = action.meta.arg.invoiceId;
        const index = state.invoices.findIndex(inv => inv._id === invoiceId);
        if (index !== -1) {
          state.invoices[index] = { ...state.invoices[index], status: 'sent' };
        }
      })
      .addCase(sendInvoice.rejected, (state, action) => {
        state.sendLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentInvoice, clearCurrentInvoice, setFilters } = invoicesSlice.actions;
export default invoicesSlice.reducer;
